import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { usePageCms } from "@/lib/usePageCms";
import { PageHero } from "@/components/site/PageHero";
import { ListingIntro } from "@/components/site/ListingIntro";
import { ListingEmpty } from "@/components/site/ListingEmpty";
import { ListingPagination } from "@/components/site/ListingPagination";
import { sortItems } from "@/lib/listing";
import { ArrowRight, Clock, CalendarDays } from "lucide-react";

export default function Destinations() {
  const FB = { overline: "Explore The State", title: "Maharashtra Destination Regions", subtitle: "Eight distinct regions across coast, mountain, forest and heritage — each ready to be tailored for your travellers.", image: "https://images.unsplash.com/photo-1663079156029-a170af256f67?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", seo_title: "Maharashtra Destinations | Maha Sampada DMC for Travel Agents", seo_description: "Explore Maharashtra's destination regions — Konkan Coast, Sahyadris, Vidarbha wildlife, spiritual circuits and more. Customized B2B itineraries for travel partners." };
  const { hero, sections } = usePageCms("destinations", FB);
  const cfg = ((sections || []).find((s) => s.kind === "listing_config")) || {};
  const perPage = cfg.per_page || 24;
  const isList = (cfg.layout || "grid") === "list";
  useSeo({ title: hero.seo_title || FB.seo_title, description: hero.seo_description || FB.seo_description });
  const [regions, setRegions] = useState([]);
  const [page, setPage] = useState(1);
  const resultsRef = useRef(null);
  useEffect(() => { api.get("/destinations").then((r) => setRegions(r.data)); }, []);
  useEffect(() => { setPage(1); }, [cfg.sort, perPage]);
  const goPage = (p) => { setPage(p); resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  const sorted = sortItems(regions, cfg.sort || "default", "name");
  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pageItems = sorted.slice((safePage - 1) * perPage, safePage * perPage);

  return (
    <div>
      <PageHero overline={hero.overline} title={hero.title} subtitle={hero.subtitle}
        image={hero.image} crumbs={[{ label: "Destinations" }]} />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <ListingIntro cfg={cfg} />
        {sorted.length === 0 ? (
          <ListingEmpty cfg={cfg} defaultTitle="No destinations to show yet" defaultBody="Please check back soon for Maharashtra destination regions." testid="destination-empty" />
        ) : (
          <>
            <div ref={resultsRef} style={{ scrollMarginTop: 100 }} className={`grid gap-8 ${isList ? "grid-cols-1" : "lg:grid-cols-2"}`}>
              {pageItems.map((r) => (
                <Link key={r.slug} to={`/destinations/${r.slug}`} data-testid={`destination-card-${r.slug}`}
                      className="hover-lift group grid overflow-hidden rounded-2xl border border-gold/25 bg-white sm:grid-cols-2">
                  <div className="img-zoom h-56 overflow-hidden sm:h-full"><img src={r.image} alt={r.name} className="h-full w-full object-cover" /></div>
                  <div className="flex flex-col justify-center p-7">
                    <div className="overline text-saffron">Region {String(regions.findIndex((x) => x.slug === r.slug) + 1).padStart(2, "0")}</div>
                    <h3 className="mt-2 font-display text-2xl font-semibold text-charcoal">{r.name}</h3>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{r.intro}</p>
                    <div className="mt-4 flex flex-wrap gap-4 text-xs text-charcoal">
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-forest" />{r.duration}</span>
                      <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5 text-forest" />{r.best_time}</span>
                    </div>
                    <span className="mt-4 inline-flex items-center gap-1 font-accent text-xs font-semibold text-saffron">Explore region <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" /></span>
                  </div>
                </Link>
              ))}
            </div>
            <ListingPagination page={safePage} totalPages={totalPages} onChange={goPage} testid="destination-pagination" />
          </>
        )}
      </section>
    </div>
  );
}
